import { type Dispatch, type SetStateAction, useEffect, useRef } from 'react';
import {
    type FiltersDeclaration,
    type InferFilterItemFromFilter,
    parseFilterItems,
    stringifyFilterItems,
} from '@adv-frontend/filters';
import { useGridSearchParams, usePropertyFromSearchParams } from '../../../lib/hooks';
import { SEARCH_PARAM_KEY_PAGE } from '../constants';

type FilterTypeOfDeclaration<TDeclaration> =
    TDeclaration extends FiltersDeclaration<infer FilterType> ? FilterType : never;

/** Задержка синхронизации динамических фильтров с параметрами грида. */
const FILTER_DEBOUNCE_MS = 400;

/*
 * Двусторонняя синхронизация динамических фильтров с query-параметром `filter`.
 * На странице параметры хранятся в URL, в модалке — в локальном контексте.
 * Изменение параметров обновляет `filterItems`, изменение в UI — параметры.
 */
export function useGridFilterItemsSearchParamsSync<
    TDeclaration extends FiltersDeclaration<FilterTypeOfDeclaration<TDeclaration>>,
>(
    filterItems: InferFilterItemFromFilter<FilterTypeOfDeclaration<TDeclaration>>[],
    setFilterItems: Dispatch<SetStateAction<InferFilterItemFromFilter<FilterTypeOfDeclaration<TDeclaration>>[]>>,
    filtersDeclaration: TDeclaration
) {
    const { searchParams, setSearchParams } = useGridSearchParams();
    const { searchParam: filterSearchParam, setSearchParam: setFilterSearchParam } = usePropertyFromSearchParams(
        searchParams,
        setSearchParams,
        'filter',
        [SEARCH_PARAM_KEY_PAGE],
        { debounceMs: FILTER_DEBOUNCE_MS }
    );

    const filterString = stringifyFilterItems(filterItems);
    const lastSyncedFilterRef = useRef(filterSearchParam);

    useEffect(() => {
        if (filterSearchParam === filterString) {
            lastSyncedFilterRef.current = filterSearchParam;

            return;
        }

        if (filterSearchParam !== lastSyncedFilterRef.current) {
            setFilterItems(parseFilterItems(filtersDeclaration, filterSearchParam));
            lastSyncedFilterRef.current = filterSearchParam;
            // Сбрасываем отложенную запись устаревшего filterString (напр. при history.back)
            setFilterSearchParam(filterSearchParam);

            return;
        }

        setFilterSearchParam(filterString);
        lastSyncedFilterRef.current = filterString;
    }, [filterSearchParam, filterString, setFilterSearchParam, setFilterItems, filtersDeclaration]);

    return { filterSearchParam, setFilterSearchParam };
}
